import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import { AppAccessDialog } from '@/components/apps/AppAccessDialog';
import type { AppSummary } from '@/lib/app-access';
import { canDeleteApp, deleteApp, setAppStatus } from '@/lib/app-manage';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import type { ToolAppStatus } from '@shared/types/tool-apps';

const ITEM_CLASS = 'block w-full px-3 py-2 text-left text-sm text-domino-ink hover:bg-domino-muted focus-visible:bg-domino-muted focus-visible:outline-none disabled:opacity-50';

/** Whether the current user created the app or manages the tenant. */
export const useCanManageApp = (appId: string | undefined): boolean => {
  const [canManage, setCanManage] = useState(false);
  useEffect(() => {
    let active = true;
    setCanManage(false);
    if (!appId) return;
    void supabase.rpc('can_manage_tool_app', { p_tool_app_id: appId })
      .then(({ data }) => { if (active) setCanManage(data === true); });
    return () => { active = false; };
  }, [appId]);
  return canManage;
};

interface AppManageMenuProps {
  app: AppSummary;
  /** The app after a change, or null once deleted. */
  onChanged: (app: AppSummary | null) => void;
  className?: string;
}

/**
 * The « … » of an app for its creator and the managers: access, publication,
 * archive and deletion (only while the app has never been used).
 */
export const AppManageMenu = ({ app, onChanged, className }: AppManageMenuProps) => {
  const [open, setOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [deletable, setDeletable] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setConfirmDelete(false);
    setError(null);
    void canDeleteApp(app.id).then((value) => { if (active) setDeletable(value); });
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent ? event.key === 'Escape' : !root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      active = false;
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open, app.id]);

  const run = async (action: () => Promise<AppSummary | null>) => {
    setPending(true);
    setError(null);
    try {
      const next = await action();
      setOpen(false);
      onChanged(next);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Le changement n’a pas pu être enregistré.');
    } finally {
      setPending(false);
    }
  };
  const changeStatus = (status: ToolAppStatus) => void run(async () => ({ ...app, status: await setAppStatus(app.id, status) }));
  const remove = () => void run(async () => { await deleteApp(app.id); return null; });

  return (
    <div ref={root} className={cn('relative shrink-0', className)}>
      <button type="button" aria-label={`Gérer ${app.title}`} aria-haspopup="menu" aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-domino-inkSoft hover:bg-domino-muted hover:text-domino-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune">
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <div role="menu" aria-label={`Gérer ${app.title}`}
          className="absolute right-0 top-11 z-30 w-60 overflow-hidden rounded-2xl border border-domino-cardBorder bg-domino-white py-1 shadow-[var(--domino-shadow-elevated)]">
          {app.status === 'archived' ? (
            <button type="button" role="menuitem" disabled={pending} className={ITEM_CLASS} onClick={() => changeStatus('published')}>Restaurer</button>
          ) : (
            <>
              <button type="button" role="menuitem" disabled={pending} className={ITEM_CLASS} onClick={() => { setOpen(false); setAccessOpen(true); }}>Accès</button>
              {app.flowId && <Link role="menuitem" to={`/forge?flowId=${encodeURIComponent(app.flowId)}`} className={ITEM_CLASS}>Modifier dans la Forge</Link>}
              {app.status === 'draft'
                ? <button type="button" role="menuitem" disabled={pending} className={ITEM_CLASS} onClick={() => changeStatus('published')}>Publier</button>
                : <button type="button" role="menuitem" disabled={pending} className={ITEM_CLASS} onClick={() => changeStatus('draft')}>Repasser en brouillon</button>}
              <button type="button" role="menuitem" disabled={pending} className={ITEM_CLASS} onClick={() => changeStatus('archived')}>Archiver</button>
            </>
          )}
          {deletable && (confirmDelete
            ? <button type="button" role="menuitem" disabled={pending} className={cn(ITEM_CLASS, 'font-semibold text-domino-prune')} onClick={remove}>Supprimer définitivement</button>
            : <button type="button" role="menuitem" disabled={pending} className={ITEM_CLASS} onClick={() => setConfirmDelete(true)}>Supprimer</button>)}
          {error && <p role="alert" className="px-3 py-2 text-xs text-domino-inkSoft">{error}</p>}
        </div>
      )}
      {accessOpen && (
        <AppAccessDialog app={app} onClose={() => setAccessOpen(false)}
          onSaved={(visibility) => { setAccessOpen(false); onChanged({ ...app, visibility }); }} />
      )}
    </div>
  );
};

/** The « … » at the top of an app's page, shown to its creator and the managers. */
export const AppPageManageMenu = ({ app, onChanged }: Omit<AppManageMenuProps, 'className'>) => {
  const canManage = useCanManageApp(app.id);
  return canManage ? <AppManageMenu app={app} onChanged={onChanged} /> : null;
};
